/**
 * Small shared pieces of the online payments screens (tracker item 38):
 * the "Charged online" badge, attempt status badge and the refund form
 * (ADMIN; reason required; full refund, then the existing void).
 */
import React, { useState } from 'react';
import * as types from '../../api/types';
import { inputClass, labelClass, secondaryButtonClass } from '../../components/formStyles';
import { getErrorMessage } from '../../utils/apiError';
import { ATTEMPT_STATUS_LABELS, attemptStatusClass, formatUsd } from './onlinePaymentsUi';
import { Toast } from '../../components/Toast';

export function ChargedOnlineBadge({ payment }: { payment: Pick<types.Payment, 'processor' | 'processorStatus'> }) {
  if (!payment.processor) return null;
  const refunded = payment.processorStatus === 'REFUNDED' || payment.processorStatus === 'REFUNDED_EXTERNALLY';
  return (
    <span className={`ml-2 inline-block rounded px-2 py-0.5 text-xs font-medium ${refunded ? 'bg-gray-100 text-gray-700' : 'bg-indigo-100 text-indigo-800'}`}>
      {refunded ? 'Refunded online' : 'Charged online'}
    </span>
  );
}

export function AttemptStatusBadge({ status }: { status: types.OnlinePaymentAttemptStatus }) {
  return <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${attemptStatusClass(status)}`}>{ATTEMPT_STATUS_LABELS[status]}</span>;
}

interface RefundFormProps {
  amount: string;
  /** The refund already happened at the processor: this only finishes the void */
  finishOnly?: boolean;
  onRefund: (reason: string) => Promise<void>;
  onCancel: () => void;
}

export function RefundForm({ amount, finishOnly, onRefund, onCancel }: RefundFormProps) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!reason.trim()) {
      setError('A reason is required');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onRefund(reason.trim());
    } catch (cause) {
      setError(getErrorMessage(cause, 'The refund could not be made'));
    } finally {
      setBusy(false);
    }
  }
  return (
    <form role="alertdialog" aria-label="Refund payment" onSubmit={(e) => void submit(e)} className="rounded border border-red-200 bg-red-50 p-3 space-y-3 text-sm">
      <p className="text-red-900">
        {finishOnly
          ? `The money was already refunded. Finish by voiding the ${formatUsd(amount)} payment (its accounting entry and fee are reversed).`
          : `Refund ${formatUsd(amount)} in full to the card, then void the payment (its accounting entry and processing fee are reversed).`}
      </p>
      <div>
        <label htmlFor="refund-reason" className={labelClass}>Reason</label>
        <input id="refund-reason" className={inputClass} value={reason} maxLength={500} disabled={busy} onChange={(e) => setReason(e.target.value)} />
      </div>
      {error && <Toast kind="error">{error}</Toast>}
      <div className="flex flex-wrap gap-3">
        <button type="submit" className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:bg-gray-400 min-h-[44px] sm:min-h-0" disabled={busy}>
          {busy ? 'Refunding…' : finishOnly ? 'Finish refund' : 'Refund'}
        </button>
        <button type="button" className={secondaryButtonClass} disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}
